import React, { useState, useLayoutEffect, useRef, useCallback } from "react";
import { View, StyleSheet, Pressable, ActivityIndicator } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { router, useGlobalSearchParams, useRootNavigationState } from "expo-router";
import { useTheme } from "@/hooks/useTheme";
import { UserType } from "@/types/db/users";
import { RelatedShiftsScreen } from "@/components/screens/shared/RelatedShiftsScreen";
import { RelatedTimecardsScreen } from "@/components/screens/shared/RelatedTimecardsScreen";
import { SmallToggle } from "@/components/SmallToggle";
import DrawerNavigator, { Drawer } from "@/components/screens/shared/DrawerNavigator";
import { UserRolesScreen } from "./UserRolesScreen";
import { EmergencyContactsTab } from "./EmergencyContactsTab";
import { UserPrivateTab } from "./UserPrivateTab";
import { UserPayTab } from "./UserPayTab";
import { RelatedReviewsScreen } from "../shared/RelatedReviewsScreen";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useToolTip } from "@/hooks/useToolTip";
import { ThemedText } from "@/components/ThemedText";

export interface UserRelatedTabProps {
  user: UserType | null;
  loading?: boolean;
}

interface HeaderButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  tooltipXOffset?: number;
}

function HeaderButton({ icon, tooltip, onPress, tooltipXOffset = -100 }: HeaderButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }} numberOfLines={1}>
          {tooltip}
        </ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x + tooltipXOffset, y: y - 36 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip, tooltipXOffset]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip]);

  return (
    <Pressable
      ref={ref}
      style={{ marginRight: 16, padding: 4 }}
      onPress={handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <MaterialCommunityIcons name={icon} size={24} color={colors.primary} />
    </Pressable>
  );
}

// Wrapper component for Shifts screen with header toggle
function ShiftsScreenWithHeader({ route }: { route?: { params?: { userId?: string } } }) {
  const [showInactive, setShowInactive] = useState(true);
  const userId = route?.params?.userId;

  return <RelatedShiftsScreen showInactive={showInactive} userId={userId} onShowInactiveChange={setShowInactive} />;
}

// Wrapper component for Time Cards screen with header toggle
function TimecardsScreenWithHeader() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const navigation = useNavigation();
  const [showApproved, setShowApproved] = useState(true);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.time_cards?.plural || "Time Cards",
      headerRight: () => (
        <View style={{ marginRight: 16 }}>
          <SmallToggle value={showApproved} onValueChange={setShowApproved} label="Show Approved" />
        </View>
      ),
    });
  }, [navigation, showApproved, colors, displayNames]);

  return <RelatedTimecardsScreen showApproved={showApproved} />;
}

// Wrapper component for Roles screen with header "Add" button
function RolesScreenWithHeader() {
  const navigation = useNavigation();
  const { roles: rolesPermissions } = usePermissions();
  const [pickerTrigger, setPickerTrigger] = useState(0);

  // Permission check - roles update permission, independent of parent edit state
  const canEditRoles = rolesPermissions.update;

  const handleAdd = useCallback(() => {
    setPickerTrigger((prev) => prev + 1);
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Roles",
      headerRight: () =>
        canEditRoles ? <HeaderButton icon="plus" tooltip="Add Role" onPress={handleAdd} tooltipXOffset={-50} /> : null,
    });
  }, [navigation, canEditRoles, handleAdd]);

  return <UserRolesScreen externalPickerTrigger={pickerTrigger} />;
}

// Wrapper component for Emergency Contacts screen with header "Add" button
function EmergencyContactsScreenWithHeader() {
  const navigation = useNavigation();
  const { user_emergency_contacts: ecPermissions } = usePermissions();
  const [addTrigger, setAddTrigger] = useState(0);

  // Use user_emergency_contacts create permission - independent of parent edit state
  const canCreate = ecPermissions.create;

  const handleAdd = useCallback(() => {
    setAddTrigger((prev) => prev + 1);
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Emergency Contacts",
      headerRight: () =>
        canCreate ? (
          <HeaderButton icon="plus" tooltip="Add Emergency Contact" onPress={handleAdd} tooltipXOffset={-120} />
        ) : null,
    });
  }, [navigation, canCreate, handleAdd]);

  return <EmergencyContactsTab externalAddTrigger={addTrigger} />;
}

// Wrapper component for Private Info screen
function PrivateInfoScreenWithHeader() {
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Private Info",
    });
  }, [navigation]);

  return <UserPrivateTab />;
}

// Wrapper component for Pay Info screen
function PayInfoScreenWithHeader() {
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Pay Info",
    });
  }, [navigation]);

  return <UserPayTab />;
}

// Wrapper component for Reviews screen with header "Add" button
function ReviewsScreenWithHeader() {
  const navigation = useNavigation();
  const { user_reviews: reviewsPermissions } = usePermissions();
  const [addTrigger, setAddTrigger] = useState(0);

  // Use user_reviews create permission - independent of parent edit state
  const canCreate = reviewsPermissions.create;

  const handleAdd = useCallback(() => {
    setAddTrigger((prev) => prev + 1);
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Reviews",
      headerRight: () =>
        canCreate ? <HeaderButton icon="plus" tooltip="Add Review" onPress={handleAdd} tooltipXOffset={-70} /> : null,
    });
  }, [navigation, canCreate, handleAdd]);

  return <RelatedReviewsScreen externalAddTrigger={addTrigger} />;
}

export function UserRelatedTab({ user, loading = false }: UserRelatedTabProps) {
  const { loading: authLoading, displayNames } = useAuth();
  const { shifts, time_cards, roles, user_emergency_contacts, user_private, user_pay, user_reviews } = usePermissions();
  const { colors } = useTheme();
  const params = useGlobalSearchParams<{ related?: string }>();
  const rootNavigationState = useRootNavigationState();

  // Wait for auth to load before rendering navigator
  if (authLoading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  // Permission checks - use from usePermissions hook
  const canReadShifts = shifts.read;
  const canReadTimeCards = time_cards.read;
  const canReadRoles = roles.read;
  const canReadEmergencyContacts = user_emergency_contacts.read;
  const canReadPrivate = user_private.read;
  const canReadPay = user_pay.read;
  const canReadReviews = user_reviews.read;

  // Build available screens based on permissions
  const availableScreens: string[] = [];
  if (canReadShifts) availableScreens.push("Shifts");
  if (canReadTimeCards) availableScreens.push("Time Cards");
  if (canReadEmergencyContacts) availableScreens.push("Emergency Contacts");
  if (canReadPrivate) availableScreens.push("Private Info");
  if (canReadPay) availableScreens.push("Pay Info");
  if (canReadReviews) availableScreens.push("Reviews");
  if (canReadRoles) availableScreens.push("Roles");

  // Validate requested route exists
  const getValidInitialRoute = (requested?: string): string | undefined => {
    if (!requested) return undefined;
    return availableScreens.find((s) => s.toLowerCase() === requested.toLowerCase());
  };

  const handleScreenChange = (screenName: string) => {
    // Only set params after Root Layout is mounted
    if (!rootNavigationState?.key) return;
    router.setParams({ related: screenName });
  };

  return (
    <DrawerNavigator
      initialRouteName={getValidInitialRoute(params.related)}
      onScreenChange={handleScreenChange}
      entityType="user"
    >
      <Drawer.Screen
        name="Shifts"
        component={ShiftsScreenWithHeader}
        initialParams={{ userId: user?.id }}
        options={{
          title: displayNames?.shifts?.plural || "Shifts",
          drawerLabel: canReadShifts ? displayNames?.shifts?.plural || "Shifts" : undefined,
        }}
      />
      <Drawer.Screen
        name="Time Cards"
        component={TimecardsScreenWithHeader}
        options={{
          title: displayNames?.time_cards?.plural || "Time Cards",
          drawerLabel: canReadTimeCards ? displayNames?.time_cards?.plural || "Time Cards" : undefined,
        }}
      />
      <Drawer.Screen
        name="Emergency Contacts"
        component={EmergencyContactsScreenWithHeader}
        options={{
          title: "Emergency Contacts",
          drawerLabel: canReadEmergencyContacts ? "Emergency Contacts" : undefined,
        }}
      />
      <Drawer.Screen
        name="Private Info"
        component={PrivateInfoScreenWithHeader}
        options={{ title: "Private Info", drawerLabel: canReadPrivate ? "Private Info" : undefined }}
      />
      <Drawer.Screen
        name="Pay Info"
        component={PayInfoScreenWithHeader}
        options={{ title: "Pay Info", drawerLabel: canReadPay ? "Pay Info" : undefined }}
      />
      <Drawer.Screen
        name="Reviews"
        component={ReviewsScreenWithHeader}
        options={{ title: "Reviews", drawerLabel: canReadReviews ? "Reviews" : undefined }}
      />
      <Drawer.Screen
        name="Roles"
        component={RolesScreenWithHeader}
        options={{ title: "Roles", drawerLabel: canReadRoles ? "Roles" : undefined }}
      />
    </DrawerNavigator>
  );
}

const styles = StyleSheet.create({});
